import $ from 'jquery'
import { Util } from 'bootstrap'
import Sortablejs from 'sortablejs'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'sortable'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Default = {
  disabled: true
}

const DefaultType = {
  direction: 'string',
  disabled: 'boolean',
  draggable: 'string',
  handle: 'string',
  list: 'string'
}

const ClassName = {
  INITIALIZED: 'sortable-initialized',
  DISABLED: 'sortable-disabled',
  ENABLED: 'sortable-enabled'
}

const Selector = {
  DATA_INIT: '[data-init="sortable"]',
  DATA_TOGGLE: '[data-toggle="sortable"]',
  INITIALIZED: `.${ClassName.INITIALIZED}`
}

const Event = {
  CLICK_DATA_API: `click${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Sortable {
  constructor(element, config) {
    this._config = this._getConfig(config)
    this._element = element
    this._sortableElement = this._getSortableElement()
    this._sortableOptions = this._getSortableOptions()
    this._sortable = this._createSortable()
    $(this._element).addClass(ClassName.INITIALIZED)
  }

  // Public

  enable() {
    $(this._element).removeClass(ClassName.DISABLED).addClass(ClassName.ENABLED)
    this._sortable.option('disabled', false)
  }

  disable() {
    $(this._element).removeClass(ClassName.ENABLED).addClass(ClassName.DISABLED)
    this._sortable.option('disabled', true)
  }

  toggle() {
    if (this._sortable.option('disabled')) {
      this.enable()
    } else {
      this.disable()
    }
  }

  toArray() {
    return this._sortable.toArray()
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }

    Util.typeCheckConfig(NAME, config, DefaultType)

    return config
  }

  _getSortableElement() {
    const selector = this._config.list
    return selector ? this._element.querySelector(selector) : this._element
  }

  _getSortableOptions() {
    const options = {
      ...this._config
    }
    delete options.init
    delete options.list
    return options
  }

  _createSortable() {
    return Sortablejs.create(this._sortableElement, this._sortableOptions)
  }

  // Static

  static _jQueryInterface(config, ...args) {
    const instances = this.map(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new Sortable(this, _config)
        $(this).data(DATA_KEY, data)
      }

      return data
    })

    if (config === 'toArray') {
      return instances[0].toArray()
    }

    if (typeof config === 'string') {
      instances.each(function () {
        if (typeof this[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        this[config](...args)
      })
    }

    return this
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

Sortable._jQueryInterface.call($(Selector.DATA_INIT))

$(document).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE, function () {
  const selector = Util.getSelectorFromElement(this)
  Sortable._jQueryInterface.call($(selector), (
    $(selector).not(Selector.INITIALIZED).length > 0 ?
      'enable' :
      'toggle'
  ))
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = Sortable._jQueryInterface
$.fn[NAME].Constructor = Sortable
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return Sortable._jQueryInterface
}

export default Sortable
